import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/useAuth';

const AUTH_REGISTER_URL = 'https://app.presales.tricentis.com/auth/register';
const AUTH_RESET_PASSWORD_URL = 'https://app.presales.tricentis.com/auth/resetpassword';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const nextPath = useMemo(() => {
    const from = location.state?.from;
    if (typeof from === 'string' && from.startsWith('/')) {
      return from === '/' ? '/dashboard' : from;
    }
    return '/dashboard';
  }, [location.state]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await login({ email, password });
      navigate(nextPath, { replace: true });
    } catch (err) {
      const raw = err?.message || '';
      const isNetworkish =
        err?.name === 'TypeError' ||
        raw.toLowerCase().includes('failed to fetch') ||
        raw.toLowerCase().includes('networkerror');

      setError(
        isNetworkish
          ? 'Unable to reach the authentication service. Please confirm the API is running and CORS is configured.'
          : (raw || 'Login failed')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-shell">
      <div className="login-shell__backdrop" aria-hidden="true"></div>
      <div className="container-fluid login-shell__container">
        <div className="row g-0 justify-content-center align-items-stretch login-shell__row">
          <div className="col-12 col-xl-10">
            <section className="login-panel shadow-lg" aria-label="TriServe Energy Sign In">
              <div className="login-panel__brand">
                <div className="login-panel__brand-inner">
                  <div className="login-panel__eyebrow">My Account Portal</div>

                  <div className="login-panel__logo-wrap">
                    <span className="login-panel__logo" aria-hidden="true">
                      <i className="bi bi-lightning-charge-fill"></i>
                    </span>
                    <div>
                      <h1 className="login-panel__title mb-1">TriServe Energy</h1>
                    </div>
                  </div>

                  <div className="login-panel__hero-copy">
                    {/* <h2 className="login-panel__hero-title mt-3 mb-2">Meter to Cash</h2> */}
                    <p className="login-panel__hero-text mb-0">
                      Manage move-ins, move-outs, address changes, billing activity, and service requests through one connected TriServe Energy & Utility Billing Platform. 
                    </p>
                  </div>



                    <div className="login-panel__hero-badge">
                      <i className="bi bi-shield-lock me-2" aria-hidden="true"></i>
                      Salesforce and SAP Integrations Enabled
                    </div>

                </div>
              </div>
              <div className="login-panel__form-column">
                <div className="login-panel__form-wrap position-relative">
                  {isSubmitting ? (
                    <div
                      className="login-panel__loading"
                      role="status"
                      aria-live="polite"
                      aria-label="Signing in"
                      data-testid="login-loading"
                    >
                      <div className="spinner-border text-tse-primary" aria-hidden="true"></div>
                      <div className="text-muted mt-2">Signing in…</div>
                    </div>
                  ) : null}

                  <div className="login-panel__form-header">
                    <p className="login-panel__form-eyebrow mb-2">Secure Sign In</p>
                    <h2 className="login-panel__form-title mb-2">Welcome back</h2>
                    <p className="login-panel__form-copy mb-0">
                      Enter your credentials to access the TriServe Energy portal.
                    </p>
                  </div>

                  {error ? (
                    <div className="alert alert-danger login-panel__alert" role="alert" data-testid="login-error">
                      {error}
                    </div>
                  ) : null}

                  <form
                    onSubmit={onSubmit}
                    aria-label="Login form"
                    aria-busy={isSubmitting}
                    className="login-panel__form"
                  >
                    <div className="mb-3">
                      <label htmlFor="loginEmail" className="form-label login-panel__label">Username or Email</label>
                      <div className="login-panel__input-wrap">
                        <span className="login-panel__input-icon" aria-hidden="true">
                          <i className="bi bi-person"></i>
                        </span>
                        <input
                          id="loginEmail"
                          className="form-control login-panel__input"
                          type="text"
                          autoComplete="username"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          disabled={isSubmitting}
                          data-testid="login-email"
                          placeholder="name@triserve.com"
                        />
                      </div>
                    </div>

                    <div className="mb-4">
                      <label htmlFor="loginPassword" className="form-label login-panel__label">Password</label>
                      <div className="login-panel__input-wrap">
                        <span className="login-panel__input-icon" aria-hidden="true">
                          <i className="bi bi-lock"></i>
                        </span>
                        <input
                          id="loginPassword"
                          className="form-control login-panel__input"
                          type="password"
                          autoComplete="current-password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          disabled={isSubmitting}
                          data-testid="login-password"
                          placeholder="Enter your password"
                        />
                      </div>
                    </div>

                    <button
                      className="btn btn-tse-primary w-100 login-panel__submit"
                      type="submit"
                      disabled={isSubmitting}
                      data-testid="login-submit"
                    >
                      {isSubmitting ? 'Signing in…' : 'Log in'}
                    </button>

                    <div className="login-panel__utility-links">
                      <a
                        className="login-panel__utility-link"
                        href={AUTH_RESET_PASSWORD_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="forgot-password-link"
                      >
                        Forgot password?
                      </a>
                      <span className="login-panel__utility-divider" aria-hidden="true">|</span>
                      <a
                        className="login-panel__utility-link"
                        href={AUTH_REGISTER_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="register-link"
                      >
                        Register
                      </a>
                    </div>

                    <div className="login-panel__support-row" aria-label="Login support options">
                      <div>
                        <div className="login-panel__support-title">Need access to TriServe Energy?</div>
                        <div className="login-panel__support-copy">
                          Solution Architects should use the Register link to self-register. For password resets, use Forgot password link. For all other issues, contact the TriServe platform team.
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
